import { Inject, Injectable } from '@angular/core';
import { FirebaseApp } from 'firebase/app';
import { map, Observable } from 'rxjs';
import { BaseModelKeys } from 'ag-common-lib/lib/models/base.model';
import {
  getUserGridSettingsCollectionPath,
  ReportGridSettings,
  ReportGridSettingsKeys,
} from 'ag-common-lib/lib/models/reporting/report-grid-settings.model';
import { FIREBASE_APP } from '../injections/firebase-app';
import { DataService } from './data.service';

/**
 * How one viewer has arranged a report's grids, stored per viewer rather than per browser.
 *
 * What is stored is only the difference from what the report ships with, so changing a
 * default in code reaches everyone who never overrode it. See the model for why that matters.
 */
@Injectable({ providedIn: 'root' })
export class ReportGridSettingsService extends DataService<ReportGridSettings> {
  constructor(@Inject(FIREBASE_APP) fireBaseApp: FirebaseApp) {
    super(fireBaseApp);
  }

  /**
   * The live document, so a change made in one browser reaches the others.
   *
   * Emits the viewer's own writes back as well, which the caller has to expect: the snapshot
   * carries `hasPendingWrites` precisely so an echo can be told from somebody else's change.
   */
  watchByReportId = (
    agentDbId: string,
    reportId: string,
  ): Observable<{ settings: ReportGridSettings | null; isLocalEcho: boolean }> => {
    this.setCollection(getUserGridSettingsCollectionPath(agentDbId));

    return this.getDocument(reportId).pipe(
      map(snapshot => ({
        settings: snapshot?.exists() ? ((snapshot.data() as ReportGridSettings) ?? null) : null,
        isLocalEcho: Boolean(snapshot?.metadata?.hasPendingWrites),
      })),
    );
  };

  save = async (agentDbId: string, settings: ReportGridSettings): Promise<ReportGridSettings | null> => {
    if (!agentDbId) {
      return null;
    }

    const reportId = settings[ReportGridSettingsKeys.reportId];
    const payload: ReportGridSettings = { ...settings, [BaseModelKeys.dbId]: reportId };

    this.setCollection(getUserGridSettingsCollectionPath(agentDbId));

    const existing = await this.getById(reportId);

    return existing ? this.updateFields(reportId, payload) : this.create(payload, reportId);
  };
}
